import { LocalStorage, } from 'quasar'
import { unref, } from 'vue'


// The permission helpers below are module-level on purpose: the router guard and
// the menu filter need them outside a component setup context, where a
// composable calling useQuasar() cannot go. The composable at the bottom keeps
// the existing component-facing API and delegates here, so there is one
// definition of "does this user hold these permissions" in the app.

// getUserPermissionSet reads the signed-in user's permissions into a lookup map.
// It never throws: a storage failure or a value that is not an array reads as
// "no permissions", which every caller treats as the restrictive answer.
export function getUserPermissionSet() {
	const set = {}
	try {
		const permissions = LocalStorage.getItem("user_permissions")
		if (!Array.isArray(permissions)) {
			return set
		}
		for (const permission of permissions) {
			set[permission] = true
		}
	} catch (e) {
		console.error("Error retrieving the user's permissions: " + e)
	}
	return set
}

// asPermissionList normalizes an argument to an array of permission strings.
// A single string becomes a one-element list rather than being iterated
// character by character — a caller that passes "user:update" instead of
// ["user:update"] must be DENIED that one permission, never silently allowed.
function asPermissionList(permissions) {
	if (permissions == null) {
		return []
	}
	return Array.isArray(permissions) ? permissions : [permissions]
}

// holdsAll reports whether the set satisfies EVERY permission listed. An empty
// or absent list is satisfied — that is how "this destination declares no
// permissions" reads throughout the app. A malformed (non-array) argument is
// normalized, so it fails CLOSED against the set rather than passing.
export function holdsAll(permissionSet, permissions) {
	for (const permission of asPermissionList(permissions)) {
		if (!permissionSet[permission]) {
			return false
		}
	}
	return true
}

// holdsAny reports whether the set satisfies AT LEAST ONE of the permissions
// listed. An empty or absent list is satisfied, for the same reason as holdsAll:
// a destination that lists nothing is not asking for anything.
export function holdsAny(permissionSet, permissions) {
	const list = asPermissionList(permissions)
	if (list.length === 0) {
		return true
	}
	for (const permission of list) {
		if (permissionSet[permission]) {
			return true
		}
	}
	return false
}

export default function () {

	// Checks if the user has the necessary permissions to access the card.
	function userHasPermissions(permissions) {
		permissions = unref(permissions)
		if (!permissions || permissions.length === 0) {
			return true
		}
		// Read and compare through the shared helpers, so a stored value that
		// is not an array degrades to "no permissions" here exactly as it does
		// for the menu and the landing page, instead of throwing mid-render.
		return holdsAll(getUserPermissionSet(), permissions)
	}

	return {
		userHasPermissions,
	}
}
